import { LoadingButton } from '@mui/lab';
import { Alert, Box, Card, CardContent, Container, Stack, TextField, Typography, useTheme } from '@mui/material';
import { useFormik } from 'formik';
import { useRecoilState } from 'recoil';
import * as yup from 'yup';
import { authState } from '../../api';
import { loadBalancerAPI } from '../../api/load-balancer';
import { StyledSmartLink } from '../../components/common';
import { useTranslation } from 'react-i18next';

export const LoginPage = () => {
  const theme = useTheme();

  const [, setAuthState] = useRecoilState(authState);
  const {
    mutate: mutateLogin,
    isLoading: isLoginLoading,
    error: loginError,
  } = loadBalancerAPI.hookWrapperMutation.useLogin();
  const { t } = useTranslation();

  const validationSchema = yup.object({
    email: yup.string().trim().required(t('loginPage.pleaseEnterTheEmail')),
    password: yup.string().trim().required(t('loginPage.pleaseEnterThePassword')),
  });

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      mutateLogin(values, {
        onSuccess: ({ data }) => {
          setAuthState((prev) => ({
            ...prev,
            ...data,
          }));
        },
      });
    },
  });

  return (
    <Container maxWidth="md">
      <Card>
        <CardContent sx={{ overflow: 'hidden', py: 5 }}>
          {loginError && <Alert severity="error"> {loginError.message}</Alert>}
          <Box
            sx={{
              mx: 'auto',
              [theme.breakpoints.up('md')]: { py: 5, width: '60%' },
              [theme.breakpoints.down('md')]: { width: '90%' },
            }}
          >
            <Typography variant="h3" sx={{ mb: 4, fontSize: '2rem' }}>
              {t('loginPage.login')}
            </Typography>
            <form onSubmit={formik.handleSubmit}>
              <Stack>
                <TextField
                  id="email"
                  name="email"
                  label={t('loginPage.email')}
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  sx={{ mb: 2 }}
                  size="small"
                />
                <TextField
                  id="password"
                  name="password"
                  label={t('loginPage.password')}
                  type="password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.password && Boolean(formik.errors.password)}
                  FormHelperTextProps={{ component: 'div' }}
                  helperText={formik.touched.password && formik.errors.password}
                  sx={{ mb: 2 }}
                  size="small"
                />
                <Box sx={{ ml: 'auto' }}>
                  <StyledSmartLink to="/auth/forgot-password">
                    <Typography variant="subtitle2">{t('loginPage.forgotPassword')}?</Typography>
                  </StyledSmartLink>
                </Box>
                <LoadingButton
                  loading={isLoginLoading}
                  color="primary"
                  variant="contained"
                  type="submit"
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  {t('loginPage.login')}
                </LoadingButton>
              </Stack>
            </form>
            <Box sx={{ mt: 2 }}>
              <hr />
            </Box>
            <Stack sx={{ flexDirection: 'row', justifyContent: 'center', mt: 2 }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {t('loginPage.dontHaveAccount')}{' '}
                <StyledSmartLink to="/auth/register">{t('loginPage.registerNow')}</StyledSmartLink>
              </Typography>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};
